import React, { ReactNode, useState } from "react";
import Badge from "../../components/badge";
import Timeline, {
  TimelineDotVariant,
  TimelineItem,
} from "../../components/timeline";
import { getDisplayStatus } from "./underwritingUtils";
import {
  PolicyAttachmentTimelineEvent,
  UnderwritingPolicyAttachmentStatus,
  TimelinePolicyDiffMapping,
} from "../../models/underwritingPolicyAttachment";
import { renderApplicabilityMetadata } from "./underwritingUtils";
import UnderwritingUiCompareLink from "../underwriting-ui/UnderwritingUiCompareLink";
import {
  findNearestPrecedingShadow,
  getActorRoleLabel,
  getEventActor,
} from "./actorUtils";

function formatDateTime(dateStr: string): string {
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const mins = String(d.getMinutes()).padStart(2, "0");
  const secs = String(d.getSeconds()).padStart(2, "0");
  return `${day}-${month}-${year} ${hours}:${mins}:${secs}`;
}

interface TimelineViewProps {
  events: PolicyAttachmentTimelineEvent[];
}

const ACTIVE_STATUSES = new Set([
  UnderwritingPolicyAttachmentStatus.ACTIVE,
  UnderwritingPolicyAttachmentStatus.ACTIVE_EXPERIMENT,
]);

const STATUS_DOT_MAP: Record<
  UnderwritingPolicyAttachmentStatus,
  TimelineDotVariant
> = {
  [UnderwritingPolicyAttachmentStatus.ACTIVE]: "success",
  [UnderwritingPolicyAttachmentStatus.ACTIVE_EXPERIMENT]: "info",
  [UnderwritingPolicyAttachmentStatus.SHADOW]: "warning",
  [UnderwritingPolicyAttachmentStatus.INACTIVE]: "danger",
};

const STATUS_BADGE_MAP: Record<
  UnderwritingPolicyAttachmentStatus,
  "success" | "pending" | "reject"
> = {
  [UnderwritingPolicyAttachmentStatus.ACTIVE]: "success",
  [UnderwritingPolicyAttachmentStatus.ACTIVE_EXPERIMENT]: "success",
  [UnderwritingPolicyAttachmentStatus.SHADOW]: "pending",
  [UnderwritingPolicyAttachmentStatus.INACTIVE]: "reject",
};

interface ActiveInterval {
  attachmentId: string;
  status: UnderwritingPolicyAttachmentStatus;
  startTime: number;
  endTime: number;
}

function buildActiveIntervals(
  events: PolicyAttachmentTimelineEvent[]
): ActiveInterval[] {
  const intervals: ActiveInterval[] = [];
  const openIntervals = new Map<string, ActiveInterval>();

  for (const event of events) {
    const ts = new Date(event.timestamp).getTime();

    if (ACTIVE_STATUSES.has(event.status)) {
      const existing = openIntervals.get(event.attachmentId);
      if (existing) {
        existing.endTime = ts;
        openIntervals.delete(event.attachmentId);
      }
      const interval: ActiveInterval = {
        attachmentId: event.attachmentId,
        status: event.status,
        startTime: ts,
        endTime: Infinity,
      };
      intervals.push(interval);
      openIntervals.set(event.attachmentId, interval);
    } else {
      const existing = openIntervals.get(event.attachmentId);
      if (existing) {
        existing.endTime = ts;
        openIntervals.delete(event.attachmentId);
      }
    }
  }

  return intervals;
}

interface ConcurrentAttachment {
  attachmentId: string;
  status: UnderwritingPolicyAttachmentStatus;
}

function getConcurrentAttachments(
  event: PolicyAttachmentTimelineEvent,
  intervals: ActiveInterval[]
): ConcurrentAttachment[] {
  const ts = new Date(event.timestamp).getTime();

  return intervals
    .filter(
      (interval) =>
        interval.attachmentId !== event.attachmentId &&
        interval.startTime <= ts &&
        interval.endTime > ts
    )
    .map((interval) => ({
      attachmentId: interval.attachmentId,
      status: interval.status,
    }));
}

function deduplicateConcurrent(
  items: ConcurrentAttachment[]
): ConcurrentAttachment[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.attachmentId)) return false;
    seen.add(item.attachmentId);
    return true;
  });
}

function PolicyDiffPanel({
  mappings,
}: {
  mappings: TimelinePolicyDiffMapping[];
}) {
  const [open, setOpen] = useState(false);
  const withDiff = mappings.filter((m) => m.oldPolicy);
  if (withDiff.length === 0) return null;

  return (
    <div className="rf-timeline-diff-panel">
      <button
        className="rf-timeline-diff-toggle"
        onClick={() => setOpen((v) => !v)}
        type="button"
      >
        <span>{open ? "\u25BE" : "\u25B8"} Policy Diffs</span>
        <span className="rf-timeline-diff-count">
          {withDiff.length} comparison{withDiff.length !== 1 ? "s" : ""}
        </span>
      </button>
      {open && (
        <table
          className="table table-sm table-bordered mt-2"
          style={{ tableLayout: "fixed", width: "100%" }}
        >
          <colgroup>
            <col style={{ width: "30%" }} />
            <col style={{ width: "30%" }} />
            <col style={{ width: "30%" }} />
            <col style={{ width: "10%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Old Policy</th>
              <th>New Policy</th>
              <th>Summary</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {withDiff.map((m) => (
              <tr key={m.newPolicy}>
                <td style={{ wordBreak: "break-all" }}>
                  <small>{m.oldPolicy}</small>
                </td>
                <td style={{ wordBreak: "break-all" }}>
                  <small>{m.newPolicy}</small>
                </td>
                <td style={{ overflow: "hidden", wordBreak: "break-all" }}>
                  {m.diff ? (
                    <div style={{ fontSize: "0.8rem" }}>
                      {m.diff.business_summary.effect && (
                        <span
                          className={`badge mb-1 ${
                            m.diff.business_summary.effect === "mixed"
                              ? "bg-warning text-dark"
                              : m.diff.business_summary.effect ===
                                "more_restrictive"
                              ? "bg-danger"
                              : m.diff.business_summary.effect === "identical"
                              ? "bg-secondary"
                              : "bg-success"
                          }`}
                        >
                          {m.diff.business_summary.effect}
                        </span>
                      )}
                      {m.diff.business_summary.key_changes.length > 0 && (
                        <ul className="mb-0 ps-3">
                          {m.diff.business_summary.key_changes.map(
                            (change, i) => (
                              <li key={i}>{change}</li>
                            )
                          )}
                        </ul>
                      )}
                    </div>
                  ) : (
                    <small className="text-muted">&mdash;</small>
                  )}
                </td>
                <td>
                  <UnderwritingUiCompareLink
                    basePolicy={m.oldPolicy}
                    targetPolicy={m.newPolicy}
                  >
                    ↗
                  </UnderwritingUiCompareLink>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function renderEventCard(
  event: PolicyAttachmentTimelineEvent,
  concurrent: ConcurrentAttachment[],
  precedingShadow?: PolicyAttachmentTimelineEvent
) {
  return (
    <>
      <div className="rf-timeline-event-header">
        <div className="rf-timeline-event-header-left">
          <span className="rf-timeline-event-title">{event.attachmentId}</span>
          <Badge type={STATUS_BADGE_MAP[event.status]} capitalize>
            {getDisplayStatus(event.status).toLowerCase()}
          </Badge>
        </div>
        <span className="rf-timeline-event-timestamp">
          {formatDateTime(event.timestamp)}
        </span>
      </div>
      <div className="rf-timeline-event-details">
        <DetailRow
          label="Policies"
          value={event.policies.join(", ")}
        />
        {event.status !== UnderwritingPolicyAttachmentStatus.SHADOW &&
          event.status !== UnderwritingPolicyAttachmentStatus.INACTIVE &&
          precedingShadow && (
            <DetailRow
              label="Requested By"
              value={`${getEventActor(precedingShadow)} (${formatDateTime(
                precedingShadow.timestamp
              )})`}
            />
          )}
        <DetailRow
          label={getActorRoleLabel(event.status)}
          value={`${getEventActor(event)} (${formatDateTime(event.timestamp)})`}
        />
        {event.description && (
          <DetailRow label="Description" value={event.description} />
        )}
        {event.experimentName && (
          <DetailRow label="Experiment" value={event.experimentName} />
        )}
        {event.applicabilityMetadata && (
          <DetailRow
            label="Applicability"
            value={renderApplicabilityMetadata(event.applicabilityMetadata)}
          />
        )}
        {event.remarks && <DetailRow label="Remarks" value={event.remarks} />}
      </div>
      {concurrent.length > 0 && (
        <div className="rf-timeline-concurrent">
          <span className="rf-timeline-concurrent-label">
            Running in parallel:
          </span>
          <div className="rf-timeline-concurrent-tags">
            {concurrent.map((c) => (
              <span
                key={c.attachmentId}
                className={`rf-timeline-concurrent-tag rf-timeline-concurrent-tag--${
                  STATUS_DOT_MAP[c.status]
                }`}
              >
                {c.attachmentId}
                <span className="rf-timeline-concurrent-tag-status">
                  {getDisplayStatus(c.status).toLowerCase()}
                </span>
              </span>
            ))}
          </div>
        </div>
      )}

      {event.policyDiffMappings && event.policyDiffMappings.length > 0 && (
        <PolicyDiffPanel mappings={event.policyDiffMappings} />
      )}
    </>
  );
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: string | ReactNode;
}) {
  return (
    <div className="rf-timeline-event-detail-row">
      <span className="rf-timeline-event-detail-label">{label}:</span>
      <span className="rf-timeline-event-detail-value">{value}</span>
    </div>
  );
}

function buildTimelineItems(
  events: PolicyAttachmentTimelineEvent[]
): TimelineItem<PolicyAttachmentTimelineEvent>[] {
  return events.map((event) => ({
    data: event,
    dotVariant: STATUS_DOT_MAP[event.status],
  }));
}

export default function TimelineView({ events }: TimelineViewProps) {
  const ascendingEvents = [...events].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );
  const descendingEvents = [...ascendingEvents].reverse();

  const items = buildTimelineItems(descendingEvents);
  const intervals = buildActiveIntervals(ascendingEvents);

  return (
    <Timeline
      items={items}
      renderItem={(event) => {
        const concurrent = deduplicateConcurrent(
          getConcurrentAttachments(event, intervals)
        );
        const sameAttachmentAscendingEvents = ascendingEvents.filter(
          (item) => item.attachmentId === event.attachmentId
        );
        const precedingShadow =
          event.status === UnderwritingPolicyAttachmentStatus.ACTIVE ||
          event.status === UnderwritingPolicyAttachmentStatus.ACTIVE_EXPERIMENT
            ? findNearestPrecedingShadow(event, sameAttachmentAscendingEvents)
            : undefined;
        return renderEventCard(event, concurrent, precedingShadow);
      }}
      emptyMessage="No timeline events found for this employer."
    />
  );
}
